import { Box, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { KitButton, KitItem } from '../common/MobileKit';
import { KitSwitch } from '../common/MobileKitExtras';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';

const ShoppingCart = protoIcon('cart');
const ClipboardList = protoIcon('orders');
const FileText = protoIcon('quote');
const Users = protoIcon('users');
const Package = protoIcon('package');
const Wallet = protoIcon('wallet');
const Car = protoIcon('car');
const Wrench = protoIcon('wrench');
const Truck = protoIcon('truck');
const Layers = protoIcon('layers');

// Atalhos do (+) editáveis: no mínimo QUICK_MIN e no máximo QUICK_MAX itens visíveis.
export const QUICK_MAX = 6;
export const QUICK_MIN = 1;
// Padrão = os atalhos que o (+) mostrava antes da edição (ver os 6 primeiros do catálogo).
const QUICK_DEFAULT = ['sale', 'order', 'quote', 'customer', 'product', 'expense'];

/**
 * Catálogo completo de atalhos de criação do botão (+) central da barra inferior
 * (`#novo-quick` de `prototipos-mobile/assets/mobile.js`). Cada ação só aparece
 * com a mesma permissão exigida pelo menu/telas de destino.
 */
export function useQuickActions() {
    const { hasPermission } = usePermissions();
    const all = [
        { id: 'sale', icon: ShoppingCart, tone: '', title: 'Vender', sub: 'Abrir o PDV', path: '/sale', allowed: hasPermission('sale.create') },
        { id: 'order', icon: ClipboardList, tone: 'purple', title: 'Gerar ordem de serviço', sub: 'Nova OS para um cliente', path: '/serviceorder/generate', allowed: hasPermission('serviceorder.create') },
        { id: 'quote', icon: FileText, tone: 'amber', title: 'Gerar orçamento', sub: 'Proposta para aprovação', path: '/servicequotes/generate', allowed: hasPermission('servicequote.create') },
        { id: 'customer', icon: Users, tone: 'green', title: 'Novo cliente', sub: 'Cadastro rápido', path: '/customers/new', allowed: hasPermission('customer.create') },
        { id: 'product', icon: Package, tone: 'gray', title: 'Novo produto', sub: 'Cadastrar no estoque', path: '/products/new', allowed: hasPermission('product.create') },
        { id: 'expense', icon: Wallet, tone: 'red', title: 'Lançar despesa', sub: 'Conta a pagar', path: '/accounts/payable', allowed: hasPermission('accounts.payable.create') },
        { id: 'vehicle', icon: Car, tone: 'purple', title: 'Novo veículo', sub: 'Cadastrar veículo', path: '/vehicles/new', allowed: hasPermission('vehicle.create') },
        { id: 'service', icon: Wrench, tone: 'amber', title: 'Novo serviço', sub: 'Cadastrar serviço', path: '/services/new', allowed: hasPermission('service.create') },
        { id: 'supplier', icon: Truck, tone: 'green', title: 'Novo fornecedor', sub: 'Cadastrar fornecedor', path: '/suppliers/new', allowed: hasPermission('supplier.create') },
        { id: 'purchase', icon: Layers, tone: 'gray', title: 'Nova compra', sub: 'Entrada manual de mercadoria', path: '/purchases/new', allowed: hasPermission('purchase.create') },
    ];
    return all.filter((a) => a.allowed);
}

function readSelection(storageKey) {
    if (!storageKey) return null;
    try {
        const parsed = JSON.parse(window.localStorage.getItem(storageKey));
        return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : null;
    } catch (e) {
        return null;
    }
}

function writeSelection(storageKey, ids) {
    if (!storageKey) return;
    try {
        window.localStorage.setItem(storageKey, JSON.stringify(ids));
    } catch (e) {
        // armazenamento indisponível: a escolha vale só até recarregar
    }
}

/**
 * Seleção dos atalhos do (+) por usuário (`storageKey`, ex.: `fv:nav:plus:<id>`).
 * `available` = atalhos com permissão (catálogo). Devolve os `visible` na ordem escolhida.
 */
export function useQuickPlusSelection(available, storageKey) {
    const [saved, setSaved] = React.useState(() => readSelection(storageKey));
    // A chave só existe depois que o usuário carrega; relê quando ela muda.
    React.useEffect(() => { setSaved(readSelection(storageKey)); }, [storageKey]);

    const availableIds = available.map((a) => a.id);
    const resolve = (ids) => ids.filter((id, i) => availableIds.includes(id) && ids.indexOf(id) === i).slice(0, QUICK_MAX);
    const defaultIds = () => {
        const d = resolve(QUICK_DEFAULT);
        return d.length ? d : availableIds.slice(0, QUICK_MAX);
    };
    let selectedIds = resolve(saved || QUICK_DEFAULT);
    if (selectedIds.length === 0) selectedIds = defaultIds();
    const visible = selectedIds.map((id) => available.find((a) => a.id === id));

    const save = (ids) => {
        writeSelection(storageKey, ids);
        setSaved(ids);
    };
    return { visible, selectedIds, defaultIds, save };
}

/**
 * Toque e segure. `onLongPress` dispara após `delay` ms sem soltar/mover; o clique que
 * o navegador gera ao soltar depois disso é suprimido (`onClick` deve usar `consumeLongPress`).
 */
export function useLongPress(onLongPress, delay = 500) {
    const timer = React.useRef(null);
    const start = React.useRef({ x: 0, y: 0 });
    const fired = React.useRef(false);
    const cbRef = React.useRef(onLongPress);
    cbRef.current = onLongPress;

    const clear = React.useCallback(() => {
        if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    }, []);
    React.useEffect(() => clear, [clear]);

    return {
        // true se este clique é o "rabo" de um long-press (e deve ser ignorado)
        consumeLongPress: () => {
            if (!fired.current) return false;
            fired.current = false;
            return true;
        },
        handlers: {
            onPointerDown: (e) => {
                if (e.pointerType === 'mouse' && e.button !== 0) return;
                fired.current = false;
                start.current = { x: e.clientX, y: e.clientY };
                clear();
                timer.current = setTimeout(() => {
                    timer.current = null;
                    fired.current = true;
                    try { if (navigator.vibrate) navigator.vibrate(15); } catch (err) { /* sem vibração */ }
                    cbRef.current();
                }, delay);
            },
            onPointerMove: (e) => {
                if (!timer.current) return;
                if (Math.abs(e.clientX - start.current.x) > 10 || Math.abs(e.clientY - start.current.y) > 10) clear();
            },
            onPointerUp: clear,
            onPointerCancel: clear,
            onPointerLeave: clear,
            // Android abre menu de contexto no toque longo
            onContextMenu: (e) => e.preventDefault(),
        },
    };
}

export default function MobileQuickActions({ open, onClose, actions }) {
    const navigate = useNavigate();
    return (
        <MobileSheet open={open} onClose={onClose} title="O que você quer fazer?">
            <Box>
                {actions.map((a) => (
                    <KitItem
                        key={a.id}
                        icon={a.icon}
                        tone={a.tone}
                        title={a.title}
                        sub={a.sub}
                        chevron
                        sx={{ border: 0, p: '12px 4px' }}
                        onClick={() => { onClose(); navigate(a.path); }}
                    />
                ))}
                <Typography sx={{ fontSize: 12, color: 'text.disabled', textAlign: 'center', pt: '10px' }}>
                    Dica: segure o + para personalizar estes atalhos.
                </Typography>
            </Box>
        </MobileSheet>
    );
}

/** Folha de edição dos atalhos do (+): switches, até QUICK_MAX, ordem de ativação. */
export function MobileQuickActionsEdit({ open, onClose, available, selectedIds, defaultIds, onSave }) {
    const [draft, setDraft] = React.useState([]);
    // Reinicia o rascunho a cada abertura.
    React.useEffect(() => { if (open) setDraft(selectedIds); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = (id) => {
        setDraft((prev) => {
            if (prev.includes(id)) return prev.length <= QUICK_MIN ? prev : prev.filter((x) => x !== id);
            return prev.length >= QUICK_MAX ? prev : [...prev, id];
        });
    };
    const full = draft.length >= QUICK_MAX;

    return (
        <MobileSheet
            open={open}
            onClose={onClose}
            title="Atalhos do +"
            footer={(
                <>
                    <KitButton variant="default" block onClick={() => setDraft(defaultIds())}>Restaurar padrão</KitButton>
                    <KitButton variant="primary" block onClick={() => { onSave(draft); onClose(); }}>Salvar</KitButton>
                </>
            )}
        >
            <Typography sx={{ fontSize: 12.5, color: full ? 'warning.main' : 'text.disabled', fontWeight: full ? 700 : 400, pb: '4px' }}>
                {full
                    ? `Limite de ${QUICK_MAX} atalhos atingido. Desative um para escolher outro.`
                    : `Escolha de ${QUICK_MIN} a ${QUICK_MAX} atalhos (${draft.length}/${QUICK_MAX}).`}
            </Typography>
            {available.map((a) => {
                const checked = draft.includes(a.id);
                return (
                    <KitSwitch
                        key={a.id}
                        title={a.title}
                        description={a.sub}
                        checked={checked}
                        disabled={checked ? draft.length <= QUICK_MIN : full}
                        onChange={() => toggle(a.id)}
                    />
                );
            })}
        </MobileSheet>
    );
}
